/* eslint-disable lit-a11y/click-events-have-key-events */
import { css, html, LitElement } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import assetsPath from '../../constant.js';

/**
 * Choice Card component for Edison.
 *
 * title - The title of the card.
 * subtitle - The subtitle of the card (optional).
 * inputName - The name of the form input (optional).
 * iconUrl - The URL to icon to display beside the title (optional).
 * iconSize - The icon width size in pixel
 * price - The price to display (optional).
 * isPromo - Display a "Promo" badge instead of price (optional).
 * isStateAid - Display a State Aid price instead of normal price (optional).
 * stateAidText - State Aid text (optional).
 * promoLabel - The promo label under the price (optional).
 * isFree - Display a "Offert" badge instead of price (optional).
 * isIncluded - Display a "inclus" badge instead of price (optional).
 * frequency - The text next to the price (optional).
 * light - Turn on the light-mode (white background) (default true).
 * actionName - The action button text (optional)
 * actionType - The action button type (optional)
 * pendingRemove - Removal is in progress (optional)
 * hideActionButton - show/hide action button (optional)
 * title-icon - The button right to the title (slot optional).
 * button-card-content - The content inside the button card. slot.
 * info - The content above action button. slot.
 * expand - The content below action button. slot.
 * warning - Show warning above action button (slot optional).
 */
@customElement('nn-choice-card')
export class ChoiceCard extends LitElement {
  static styles = css`
    body {
      margin: 0;
      padding: 0;
    }
    .button-card__price[variant='dark'] {
      margin-top: 8px;
    }
    .button-card {
      align-items: flex-start;
      background: var(--nn-choice-card_button-card_background, #f7f8fb);
      border: 1px solid var(--nn-choice-card_button-card_border, #e8e8e8);
      border-radius: var(--nn-choice-card_button-card_border-radius, 16px);
      box-sizing: border-box;
      display: flex;
      flex-direction: row;
      flex-wrap: wrap;
      font-family: 'font-medium';
      gap: var(--nn-choice-card_button-card_gap, 5px);
      height: var(--nn-choice-card_button-card_height, 100%);
      justify-content: start;
      padding: var(--nn-choice-card_button-card_padding, 12px);
      text-align: left;
      transition: all ease-in-out
        var(--nn-choice-card_button-card_transition, 0.3s);
    }
    .button-card--small-font .button-card__title {
      font-size: var(
        --nn-choice-card_button-card--small-font_button-card__title_font-size,
        14px
      );
      line-height: 16px;
    }
    .button-card--small-font .button-card__price {
      font-size: var(
        --nn-choice-card_button-card--small-font_button-card__price_font-size,
        14px
      );
      line-height: 16px;
    }
    .button-card__wrapper {
      align-items: center;
      display: flex;
      flex-wrap: wrap;
      justify-content: center;
      width: 100%;
    }
    .button-card__wrapper--left {
      cursor: pointer;
    }
    .button-card__wrapper--left:empty {
      display: none;
    }
    .button-card__wrapper--middle {
      display: none;
    }
    .button-card__wrapper--right {
      align-items: flex-start;
      display: flex;
      flex: 1;
      flex-direction: row;
      flex-wrap: wrap;
      width: 100%;
    }
    .button-card--light {
      background: var(--nn-choice-card_button_card--light_background, #fff);
    }
    .button-card__container--left {
      display: flex;
      flex: 3 1 50%;
      flex-direction: row;
      flex-wrap: nowrap;
      gap: var(--nn-choice-card_button_card__container--left_gap, 10px);
      justify-content: space-between;
      order: 1;
    }
    .button-card__container--right {
      align-items: flex-start;
      display: flex;
      flex: 2 1 25%;
      flex-direction: column;
      order: 3;
    }
    .button-card__container--bottom {
      flex-basis: 100%;
      max-width: 100%;
      order: 2;
    }

    .button-card--multiple-price .button-card__container--left {
      flex: 3 1 100%;
    }
    .button-card--multiple-price .button-card__container--right {
      align-items: flex-end;
      margin-top: var(
        --nn-choice-card_button-card--multiple-price_button-card__container--right,
        35px
      );
      order: 2;
    }
    .button-card--multiple-price .button-card__container--bottom {
      order: 3;
    }
    .button-card--divertissement-pack.button-card--4
      .button-card__container--right,
    .button-card--wibox-tv.button-card--4 .button-card__container--right {
      margin-bottom: var(
        --nn-choice-card_button-card--packs_button-card__container--right_margin-bottom,
        24px
      );
    }
    .button-card__title_group {
      display: flex;
      flex-direction: column;
      flex-grow: 2;
      margin-bottom: var(
        --nn-choice-card_button-card__title_group_margin-bottom,
        auto
      );
      margin-top: var(
        --nn-choice-card_button-card__title_group_margin-top,
        auto
      );
    }
    .button-card__title {
      font-family: 'font-medium';
      font-size: var(--nn-choice-card_button-card__title_font-size, 18px);
      font-weight: bold;
      margin-bottom: var(
        --nn-choice-card_button-card__title_margin-bottom,
        auto
      );
      margin-top: var(--nn-choice-card_button-card__title_margin-top, auto);
    }
    .button-card__title.button-card__choice_pair {
      font-size: var(
        --nn-choice-card_button-card__title-button-card__choice_pair_font-size,
        18px!important
      );
    }
    .button-card__subtitle {
      font-family: 'font-light';
      color: var(--nn-choice-card_button-card__subtitle_color, #494949);
      font-size: var(--nn-choice-card_button-card__subtitle_font-size, 14px);
    }
    .button-card--disabled {
      background: var(
        --nn-choice-card_button-card--disabeld_background,
        rgba(0, 0, 0, 0)
      );
      cursor: not-allowed !important;
      opacity: 0.5;
    }
    .button-card--disabled .button-card__title {
      cursor: not-allowed;
    }
    .button-card--disabled .button-card__question {
      pointer-events: none;
    }
    .button-card:hover {
      border: 1px solid
        var(--nn-choice-card_button-card--hover_border-color, #598be7);
    }
    .button-card-no-hover:hover {
      border: 1px solid
        var(--nn-choice-card_button-card-no-hover--hover_border-color, #e8e8e8);
    }
    .button-card--disabled:hover {
      border: 1px solid
        var(--nn-choice-card_button-card--disabled--hover_border-color, #e8e8e8);
    }
    .button-card--disabled .button-card__question {
      cursor: not-allowed;
    }
    .button-card--1 {
      box-shadow: 0 9px 10px rgba(54, 54, 54, 0.09);
    }
    .button-card--5 {
      box-shadow: 0 9px 10px rgba(54, 54, 54, 0.09);
    }
    .button-card--2 {
      border: 2px solid
        var(--nn-choice-card_button-card--2_border-color, #598be7);
      padding: var(--nn-choice-card_button-card--2_padding, 15px 16px);
    }
    .button-card--2:hover {
      border: 2px solid
        var(--nn-choice-card_button-card--2--hover_border-color, #598be7);
    }
    .button-card--3 .button-card__title,
    .button-card--4 .button-card__title,
    .button-card--6 .button-card__title {
      font-size: var(
        --nn-choice-card_button-card--all_button-card__title_font-size,
        14px
      );
      font-weight: bold;
    }
    .button-card--3 .button-card__price,
    .button-card--4 .button-card__price,
    .button-card--6 .button-card__price {
      font-size: var(
        --nn-choice-card_button-card--all_button-card__price_font-size,
        14px
      );
    }
    .button-card--3:hover,
    .button-card--4:hover {
      border: 1px solid
        var(--nn-choice-card_button-card--all--hover_border-color, #e8e8e8);
    }
    .button-card--6,
    .button-card--6:hover {
      border: 2px solid
        var(--nn-choice-card_button-card--6--hover_border-color, #598be7);
    }
    .button-card--3 .button-card__price .button-card__frequency,
    .button-card--4 .button-card__price .button-card__frequency,
    .button-card--6 .button-card__price .button-card__frequency {
      font-weight: normal;
    }
    .button-card__content {
      font-family: 'font-light';
      font-size: var(--nn-choice-card_button-card__content_font-size, 14px);
    }
    .state-aid {
      align-items: baseline;
      display: flex;
      flex-wrap: wrap;
      line-height: 1rem;
    }
    .state-aid__price {
      color: var(--nn-choice-card_state-aid__price_color, #000);
      font-family: 'font-heavy';
      font-size: var(--nn-choice-card_state-aid__price_font-size, 16px);
      line-height: 23px;
      margin-right: var(--nn-choice-card_state-aid__price_margin-right, 12px);
    }
    .state-aid__title {
      color: var(--nn-choice-card_state-aid__title_color, #494949);
      font-family: 'font-light', sans-serif;
      font-size: var(--nn-choice-card_state-aid__title_font-size, 11px);
    }
    button {
      border-radius: var(--nn-choice-card_button_border-radius, 32px);
      border: 1px solid var(--nn-choice-card_button_border-color, #000);
      cursor: pointer;
      font-family: 'font-medium';
      font-size: var(--nn-choice-card_button_font-size, 14px);
      height: var(--nn-choice-card_button_height, 32px);
      min-width: var(--nn-choice-card_button_min-width, 110px);
      padding: var(--nn-choice-card_button_padding, 6px 32px);
      transition: var(--nn-choice-card_button_transition, 0.3s);
    }
    .button-plus {
      background: var(--nn-choice-card_button-plus_background, transparent);
      border: var(--nn-choice-card_button-plus_border, none);
    }
    .button-card__action--1 {
      background: var(--nn-choice-card_button-card__action--1_background, #000);
      color: var(--nn-choice-card_button-card__action--1_color, #fff);
      margin-top: var(--nn-choice-card_button-card__action--1_margin-top, 8px);
    }
    .button-card__action--2 {
      background: var(--nn-choice-card_button-card__action--2_background, #fff);
      border: 2px solid;
      color: var(--nn-choice-card_button-card__action--2_color, #000);
      margin-top: var(--nn-choice-card_button-card__action--2_margin-top, 8px);
    }
    .button-card__action--3,
    .button-card__action--4,
    .button-card__action--6 {
      background-color: unset;
      border: var(--nn-choice-card_button-card__action--all_border, none);
      color: var(--nn-choice-card_button-card__action--all_color, #000);
      font-size: var(--nn-choice-card_button-card__action--all_font-size, 12px);
      font-weight: bold;
      height: unset;
      padding: var(--nn-choice-card_button-card__action--all_padding, 0);
      text-decoration: underline;
      width: unset;
    }
    .button-card__action--1:hover {
      background: var(
        --nn-choice-card_button-card__action--1--hover_background,
        #fff
      );
      color: var(--nn-choice-card_button-card__action--1--hover_color, #000);
    }
    .button-card__action--2:hover {
      background: var(
        --nn-choice-card_button-card__action--2--hover_background,
        #000
      );
      color: var(--nn-choice-card_button-card__action--2--hover_color, #fff);
      border: 2px solid
        var(--nn-choice-card_button-card__action--2--hover_border-color, #000);
    }
    .button-card--disabled .button-card__action--1:hover {
      background: var(
        --nn-choice-card_button-card--disabled_button-card__action--1--hover_background,
        #000
      );
      color: var(
        --nn-choice-card_button-card--disabled_button-card__action--1--hover_color,
        #fff
      );
    }
    .button-card--disabled button {
      cursor: not-allowed;
    }
    .button-card__question {
      display: inline-flex;
      cursor: pointer;
    }
    .button-card__question-icon {
      margin: var(
        --nn-choice-card_button-card__question-icon_margin,
        2px 5px 0
      );
    }
    .button-card--small-font .button-card__question-icon {
      margin-top: var(
        --nn-choice-card_button-card--small-font_button-card__question-icon_margin-top,
        -1px
      );
    }
    .button-card__price {
      flex: 3 1 0%;
      font-family: 'font-heavy';
      font-size: var(--nn-choice-card_button-card__price_font-size, 16px);
      line-height: 1rem;
      margin: var(--nn-choice-card_button-card__price_margin, 0);
    }
    .button-card__price--light {
      font-family: 'font-light';
    }
    .button-card__price--promo {
      flex: 2 1 0%;
      font-family: 'font-light';
      font-size: var(
        --nn-choice-card_button-card__price--promo_font-size,
        11px
      );
      margin-top: var(
        --nn-choice-card_button-card__price--promo_margin-top,
        2px
      );
      text-align: right;
    }
    .button-card__price--promo .promo-label {
      display: flex;
      flex-direction: column;
      gap: var(--nn-choice-card_button-card__price--promo_promo-label_gap, 4px);
    }
    .button-card__container--action {
      flex-basis: 100%;
      order: 5;
      text-align: center;
    }
    .button-card__warning {
      color: var(--nn-choice-card_button-card__warning_color, #d93939);
      flex-basis: 100%;
      font-family: 'font-medium';
      font-size: var(--nn-choice-card_button-card__warning_font-size, 12px);
      order: 5;
      text-align: center;
    }
    .button-card__plus {
      background-image: url(assetsPath + '/icons/plus-button.svg');
      background-repeat: no-repeat;
      height: var(--nn-choice-card_button-card__plus_height, 100px);
      width: var(--nn-choice-card_button-card__plus_width, 100px);
    }
    .button-card__price--promo {
      line-height: 1rem;
      margin: var(--nn-choice-card_button-card__price-promo_margin, 0);
      text-align: left;
    }
    .button-card__image,
    .button-card__icon.multi,
    .button-card__wrapper--middle {
      display: none;
    }
    .button-card__wrapper--right {
      padding-left: var(
        --nn-choice-card_button-card__wrapper--right_padding-left,
        8px
      );
    }
    .bold {
      font-family: 'font-medium';
      font-weight: bold;
    }
    .button-card--multiple-price .button-card__wrapper--right {
      position: relative;
    }
    @media screen and (min-width: 632px) {
      .button-card--multiple-price .button-card__container--left {
        flex: 3 1 50%;
      }
      .button-card--multiple-price .button-card__container--right {
        margin-top: 0;
      }
    }

    @media screen and (min-width: 768px) {
      :host {
        --nn-choice-card_button-card_gap: 0;
        --nn-choice-card_button-card_padding: 16px 17px;
      }
      nn-badge.button-card__price {
        margin: var(
          --nn-choice-card_nn-badge_button-card__price_margin,
          0 0 6px
        );
      }
      .button-card__price--promo {
        text-align: right;
      }
      .button-card__image {
        display: flex;
        margin: var(--nn-choice-card_button-card__image_margin, 0 auto);
        max-height: 100%;
        max-width: var(--nn-choice-card_button-card__image_max-width, 50px);
      }
      .button-card__wrapper--middle {
        display: block;
        min-width: var(
          --nn-choice-card_button-card__wrapper--middle_min-width,
          80px
        );
      }
      .button-card__wrapper--middle:empty {
        display: none;
      }
      .button-card__wrapper--right {
        margin: 0;
        padding: 0;
      }
      .button-card__container--left {
        order: 1;
      }
      .button-card__container--right {
        align-items: flex-end;
        order: 2;
      }
      .button-card--divertissement-pack.button-card--4
        .button-card__container--right,
      .button-card--wibox-tv.button-card--4 .button-card__container--right {
        margin-bottom: 0;
      }
      .button-card__wrapper.gutter {
        gap: var(--nn-choice-card_button-card__wrapper-gutter_gap, 15px);
      }
      .button-card__icon.multi {
        display: block;
      }
      .state-aid {
        align-items: flex-end;
        display: flex;
        flex-direction: column;
        text-align: right;
      }
      .state-aid__price {
        margin-right: 0;
      }
    }
  `;

  @property({ type: String }) title = 'Title';

  @property({ type: String }) subtitle = '';

  @property({ type: String }) inputName = (Math.random() + 1)
    .toString(36)
    .substring(7);

  @property({ type: String }) iconUrl = '';

  @property({ type: String }) iconSize = '35px';

  @property({ type: String }) imageUrl = '';

  @property({ type: String }) imageSize = '60px';

  @property({ type: String }) price = '';

  @property({ type: String }) promoChip = '1er mois offert';

  @property({ type: String }) promoLabel = '';

  @property({ type: String }) stateAidText = '';

  @property({ type: String }) frequency = '';

  @property({ type: Boolean }) isPromo = false;

  @property({ type: Boolean }) isRenewedPromo = false;

  @property({ type: Boolean }) isStateAid = false;

  @property({ type: Boolean }) isFree = false;

  @property({ type: Boolean }) isIncluded = false;

  @property({ type: Boolean }) isMoisOffert = false;

  @property({ type: Boolean }) light = true;

  @property({ type: Boolean }) disabled = false;

  @property({ type: String }) actionName = '';

  @property({ type: Number }) actionType = 0;

  @property({ type: Boolean }) pendingRemove = false;

  @property({ type: Boolean }) hideActionButton = false;

  @property({ type: Boolean }) isChoiceInPair = false;

  @property({ type: String }) extraClass = '';

  renderPromoBadge() {
    if (this.isMoisOffert) {
      return html`
        <nn-badge
          class="button-card__price"
          text="${this.promoChip}"
          variant="pink"
        ></nn-badge>
      `;
    }
    return html`
      <nn-badge
        class="button-card__price"
        text="Promo"
        variant="pink"
      ></nn-badge>
    `;
  }

  renderSubTitle() {
    if (this.subtitle) {
      return html`
        <div class="button-card__subtitle" .innerHTML="${this.subtitle}"></div>
      `;
    }
    return '';
  }

  renderPrice() {
    if (this.isStateAid) {
      return html`
        <div class="state-aid">
          <span class="state-aid__price"
            >${this.price}
            <span class="state-aid__frequency"
              >${this.frequency ? `/${this.frequency}` : ''}</span
            ></span
          >
          <span class="state-aid__title">${this.stateAidText}</span>
        </div>
      `;
    }
    if (this.isFree) {
      return html`
        <nn-badge
          class="button-card__price"
          text="Offert"
          variant="pink"
        ></nn-badge>
      `;
    }
    if (this.isIncluded) {
      return html`
        <nn-badge
          class="button-card__price"
          text="Inclus"
          variant="dark"
        ></nn-badge>
      `;
    }
    if (this.price && this.frequency) {
      return html`
        ${(this.isPromo && !this.isRenewedPromo) || this.isMoisOffert
          ? this.renderPromoBadge()
          : ''}
        <p class="button-card__price">
          ${this.isMoisOffert
            ? html`<span class="button-card__price--light">puis</span> `
            : ''}${this.price}<span class="button-card__frequency"
            >/${this.frequency}</span
          >
        </p>
      `;
    }
    if (this.price) {
      return html` <p class="button-card__price">${this.price}</p>`;
    }
    return '';
  }

  renderPromoLabel() {
    if (this.promoLabel) {
      return html`
        <p class="button-card__price--promo">
          ${this.parseHtml(this.promoLabel)}
        </p>
      `;
    }
    return '';
  }

  renderActionButton() {
    if (this.pendingRemove) {
      return html`
        <span class="button-card__content"
          >Votre demande de résiliation est en cours de traitement</span
        >
      `;
    }
    if (
      this.actionType > 0 &&
      this.actionType !== 5 &&
      !this.hideActionButton
    ) {
      return html`
        <button
          type="button"
          @click="${this._dispatchButtonClick}"
          class="button-card__action--${this.actionType}"
        >
          ${this.actionName}
        </button>
      `;
    }
    return '';
  }

  renderPlusButton() {
    if (this.actionType === 5) {
      return html`
        <img
          @click="${this._dispatchPlusButtonClick}"
          width="'35px'"
          src="${assetsPath}/icons/plus-button.svg"
          alt=""
        />
      `;
    }
    return html``;
  }

  renderIcon() {
    if (this.iconUrl) {
      return html`
        <img
          class="button-card__icon ${this.actionType === 5 ? 'multi' : ''}"
          width="${this.iconSize}"
          src="${this.iconUrl}"
          alt=""
        />
      `;
    }
    return html``;
  }

  renderImage() {
    if (this.imageUrl) {
      return html`
        <img
          class="button-card__image"
          width="${this.imageSize}"
          src="${this.imageUrl}"
          alt=""
        />
      `;
    }
    return html``;
  }

  render() {
    const [remainingTitle, lastTitleWord] = this.title.split(
      /\s(?=\S+[\s]+$)|\s(?=\S+$)/
    );
    return html`
      <label
        class="button-card ${this.hideActionButton
          ? 'button-card-no-hover'
          : ''} ${this.light ? 'button-card--light' : ''} ${this.actionType > 0
          ? `button-card--${this.actionType}`
          : 'button-card--small-font'} ${this.disabled
          ? ' button-card--disabled'
          : ''} ${this.extraClass ? `${this.extraClass}` : ''}"
        for="${this.inputName}"
      >
        <div
          class="button-card__wrapper ${(!this.imageUrl ||
            this.imageUrl === '') &&
          this.actionType === 5
            ? 'gutter'
            : ''}"
        >
          <div class="button-card__wrapper--left">
            ${this.renderPlusButton()}
          </div>
          <div class="button-card__wrapper--middle">${this.renderImage()}</div>
          <div class="button-card__wrapper--right">
            <div class="button-card__container--left">
              ${this.renderIcon()}
              <div class="button-card__title_group">
                <p
                  class="button-card__title${this.isChoiceInPair
                    ? ' button-card__choice_pair'
                    : ''}"
                >
                  ${remainingTitle}
                  <span
                    class="button-card__question"
                    @click="${this._dispatchQuestionClick}"
                  >
                    ${lastTitleWord}
                    <span class="button-card__question-icon"
                      ><slot name="title-icon"></slot
                    ></span>
                  </span>
                </p>
                ${this.renderSubTitle()}
              </div>
            </div>
            <div class="button-card__container--right">
              ${this.renderPrice()} ${this.renderPromoLabel()}
            </div>
            <div class="button-card__container--bottom">
              <slot
                name="button-card-content"
                class="button-card__content"
              ></slot>
            </div>
          </div>
        </div>
        <slot name="warning"></slot>
        <div class="button-card__container--action">
          <slot
            name="info"
            class="${this.disabled ? ' button-card--disabled' : ''}"
          ></slot>
          ${this.renderActionButton()}
          <slot name="expand"></slot>
        </div>
      </label>
    `;
  }

  _dispatchQuestionClick(e: Event) {
    e.preventDefault();
    const event = new CustomEvent('onClickInfo');
    this.dispatchEvent(event);
  }

  _dispatchButtonClick(e: Event) {
    e.preventDefault();
    if (this.disabled) return;
    const event = new CustomEvent('onButtonClick');
    this.dispatchEvent(event);
  }

  _dispatchPlusButtonClick(e: Event) {
    e.preventDefault();
    if (this.disabled) return;
    const event = new CustomEvent('onPlusButtonClick');
    this.dispatchEvent(event);
  }

  // eslint-disable-next-line class-methods-use-this
  parseHtml(str?: any) {
    const parser = new DOMParser();
    const doc = parser.parseFromString(str, 'text/html');
    return doc.body;
  }
}
